/**
 * <s-review-form> — Sends a product review to Judge.me from the product page.
 *
 * Attributes (on <s-review-form>):
 *   data-endpoint        — Judge.me public review endpoint, written by Liquid.
 *   data-shop-domain     — The shop's myshopify domain.
 *   data-product-id      — The Shopify product ID the review is for.
 *   data-error-message   — Translated message shown when the request fails.
 *
 * Required child markup:
 *   form                 — Fields named name, email, rating, title, body.
 *   [data-status]        — Live region for the success or error message.
 *   [data-success]       — Hidden panel shown after a successful submission.
 *
 * The request is sent as form-urlencoded so the browser treats it as a simple
 * cross-origin request (Judge.me allows any origin). Judge.me moderates the
 * review before it is published, then syncs it back into the product's review data.
 */
class ReviewForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-status]');
    this.success = this.querySelector('[data-success]');
    this.submitButton = this.form?.querySelector('[type="submit"]');
    this.writeAnotherButton = this.querySelector('[data-write-another]');
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleWriteAnother = this.handleWriteAnother.bind(this);
    this.form?.addEventListener('submit', this.handleSubmit);
    this.writeAnotherButton?.addEventListener('click', this.handleWriteAnother);
    this.setupStars();
  }

  // Star rating: buttons fill up to the hovered or chosen star and store the value in the hidden rating input.
  setupStars() {
    this.ratingInput = this.querySelector('[data-rating-input]');
    this.stars = Array.from(this.querySelectorAll('[data-star]'));
    const group = this.querySelector('[data-rating-stars]');
    this.stars.forEach((star) => {
      const value = Number(star.dataset.star);
      star.addEventListener('click', () => this.setRating(value));
      star.addEventListener('mouseenter', () => this.paintStars(value));
      star.addEventListener('focus', () => this.paintStars(value));
      star.addEventListener('keydown', (event) => {
        const current = Number(this.ratingInput?.value || 5);
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') { event.preventDefault(); this.setRating(Math.min(5, current + 1), true); }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') { event.preventDefault(); this.setRating(Math.max(1, current - 1), true); }
      });
    });
    group?.addEventListener('mouseleave', () => this.paintStars(Number(this.ratingInput?.value || 5)));
    this.setRating(Number(this.ratingInput?.value || 5));
  }

  setRating(value, moveFocus = false) {
    if (this.ratingInput) this.ratingInput.value = String(value);
    this.stars.forEach((star) => star.setAttribute('aria-checked', String(Number(star.dataset.star) === value)));
    this.paintStars(value);
    if (moveFocus) this.stars[value - 1]?.focus();
  }

  paintStars(value) {
    this.stars.forEach((star) => {
      star.style.color = Number(star.dataset.star) <= value ? 'var(--color-accent, #2563eb)' : '#d1d5db';
    });
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.writeAnotherButton?.removeEventListener('click', this.handleWriteAnother);
  }

  // Reopen a fresh form after a successful submission, keeping the reviewer's name and email.
  handleWriteAnother() {
    const name = this.form.elements.namedItem('name');
    const email = this.form.elements.namedItem('email');
    const savedName = name ? name.value : '';
    const savedEmail = email ? email.value : '';
    this.form.reset();
    if (name) name.value = savedName;
    if (email) email.value = savedEmail;
    this.setRating(5);
    this.status.textContent = '';
    this.success.hidden = true;
    this.form.hidden = false;
    this.form.querySelector('textarea[name="body"]')?.focus();
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;

    const data = new FormData(this.form);
    const params = new URLSearchParams({
      shop_domain: this.dataset.shopDomain,
      platform: 'shopify',
      id: this.dataset.productId,
      name: String(data.get('name') || '').trim(),
      email: String(data.get('email') || '').trim(),
      rating: String(data.get('rating') || ''),
      title: String(data.get('title') || '').trim(),
      body: String(data.get('body') || '').trim(),
    });

    this.setBusy(true);
    this.status.textContent = '';

    try {
      const response = await fetch(this.dataset.endpoint, { method: 'POST', body: params });
      if (!response.ok) {
        let message = this.dataset.errorMessage;
        try {
          const json = await response.json();
          if (json && json.message) message = json.message;
        } catch (_) {
          // Keep the translated fallback message.
        }
        throw new Error(message);
      }
      this.form.hidden = true;
      this.success.hidden = false;
      this.success.focus();
    } catch (error) {
      this.status.textContent = error instanceof Error && error.message ? error.message : this.dataset.errorMessage;
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
    if (this.submitButton) this.submitButton.toggleAttribute('disabled', busy);
  }
}

if (!customElements.get('s-review-form')) customElements.define('s-review-form', ReviewForm);
